import { memo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, Button, Popover, PopoverContent, PopoverTrigger } from '@scio3/ui';
import { X, Plus, Maximize2, Minimize2, History, MessageSquare } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { formatWithPattern } from '@/lib/format-date';
import { useOptibotStore } from '../stores/optibot-store';
import { SUPPORT_PHONE_TEL, SUPPORT_EMAIL, getChatApiUrl } from '../utils/constants';

interface ConversationThread {
  threadId: string;
  subject: string;
  lastMessagePreview: string;
  createdAt: string;
  updatedAt: string;
}

interface OptiBotHeaderProps {
  onNewChat?: () => void;
  onLoadConversation?: (threadId: string) => void;
  activeThreadId?: string | null;
  hasMessages?: boolean;
}

/**
 * Panel header with title, history dropdown, new chat button,
 * expand/collapse toggle, close button, and contact support info block.
 * Wrapped in React.memo to prevent re-renders when only the input state changes in the parent.
 */
export const OptiBotHeader = memo(function OptiBotHeader({
  onNewChat,
  onLoadConversation,
  activeThreadId,
  hasMessages,
}: OptiBotHeaderProps) {
  const { t } = useTranslation();
  const setOpen = useOptibotStore((s) => s.setOpen);
  const isExpanded = useOptibotStore((s) => s.isExpanded);
  const setExpanded = useOptibotStore((s) => s.setExpanded);

  return (
    <div className="shrink-0 border-b border-border">
      {/* Title bar */}
      <div className="flex items-center justify-between px-4 py-3">
        <h2 className="text-lg font-semibold text-foreground">{t('optibot.title')}</h2>
        <div className="flex items-center gap-1">
          <HistoryDropdown
            onLoadConversation={onLoadConversation}
            activeThreadId={activeThreadId}
            hasMessages={hasMessages}
          />
          {onNewChat && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onNewChat}
              aria-label={t('optibot.newChat')}
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setExpanded(!isExpanded)}
            aria-label={isExpanded ? t('optibot.collapse') : t('optibot.expand')}
          >
            {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setOpen(false)}
            aria-label={t('optibot.closePanel')}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Support info — compact 2-line layout */}
      <div className="border-t border-border bg-background px-4 py-2.5">
        <p className="text-xs text-muted-foreground">
          {t('optibot.contactSupport')} –{' '}
          <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-primary hover:underline">
            {t('optibot.supportPhone')}
          </a>
        </p>
        <p className="text-xs text-muted-foreground">
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
            {t('optibot.supportEmail')}
          </a>
          {' – '}
          {t('optibot.supportHours')}
        </p>
      </div>
    </div>
  );
});

// --- History Dropdown (Popover with lazy-loaded thread list) ---

function HistoryDropdown({
  onLoadConversation,
  activeThreadId,
  hasMessages,
}: {
  onLoadConversation?: (threadId: string) => void;
  activeThreadId?: string | null;
  hasMessages?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState<ConversationThread[]>([]);

  // Fetch on mount — only show button if threads exist
  useEffect(() => {
    const controller = new AbortController();
    const fetchHistory = async () => {
      try {
        const apiUrl = getChatApiUrl() + '/history';
        const token = useAuthStore.getState().token;
        if (!token) return;
        const res = await fetch(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            data.sort(
              (a: ConversationThread, b: ConversationThread) =>
                new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
            );
            setThreads(data);
          }
        }
      } catch (err) {
        console.warn('[OptiBot] Failed to fetch conversation history:', err);
      }
    };
    fetchHistory();
    return () => controller.abort();
  }, []);

  // No threads → no button
  if (threads.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={t('optibot.recentConversations')}>
          <History className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        <div className="border-b border-border px-3 py-2">
          <p className="text-xs font-medium text-muted-foreground">
            {t('optibot.recentConversations')}
          </p>
        </div>
        <div className="thin-scrollbar max-h-60 overflow-y-auto pt-1 pb-2">
          {threads.map((thread, index) => {
            // Active if explicitly matches, or first thread when chat has messages but no explicit threadId
            const isActive = activeThreadId
              ? activeThreadId === thread.threadId
              : hasMessages && index === 0;
            return (
              <button
                key={thread.threadId}
                type="button"
                disabled={isActive}
                className={cn(
                  'flex w-full items-start gap-2 px-3 py-2 text-left transition-colors',
                  isActive ? 'bg-primary/10 cursor-default' : 'hover:bg-muted/50',
                )}
                onClick={() => {
                  if (isActive) return;
                  onLoadConversation?.(thread.threadId);
                  setOpen(false);
                }}
              >
                <MessageSquare
                  className={cn(
                    'mt-0.5 h-3 w-3 shrink-0',
                    isActive ? 'text-primary' : 'text-muted-foreground',
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      'truncate text-xs font-medium',
                      isActive ? 'text-primary' : 'text-foreground',
                    )}
                  >
                    {thread.subject}
                  </p>
                  {thread.updatedAt && (
                    <p className="text-[10px] text-muted-foreground">
                      {`Last activity: ${formatWithPattern(thread.updatedAt, 'MM/DD/YY, h:mm A')}`}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
